/**
 * PopupElement - Custom Web Component
 * Reusable popup component with trigger and content slots
 */

class PopupElement extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.trigger = null;
    this.content = null;
    this.popup = null;
    this.swatchHandlers = new Map();
    this.sliderHandlers = new Map();
  }

  connectedCallback() {
    this.trigger = this.querySelector('[slot="trigger"]');
    this.popup = this.querySelector('.popup');
    
    if (!this.trigger || !this.popup) return;

    this.setupEventListeners();
    this.initLookbookSwatches();
  }

  disconnectedCallback() {
    this.removeEventListeners();
    this.removeLookbookSwatchesListeners();
  }

  setupEventListeners() {
    if (!this.trigger) return;
    
    // Show popup on trigger click
    this.trigger.addEventListener('click', this.handleTriggerClick.bind(this));

    // Close on overlay click or elements marked with data-popup-close
    this.popupClickHandler = this.handlePopupClick.bind(this);
    if (this.popup) {
      this.popup.addEventListener('click', this.popupClickHandler);
    }

    // Hide popup when clicking outside
    this.documentClickHandler = this.handleDocumentClick.bind(this);
    document.addEventListener('click', this.documentClickHandler);

    // Hide popup on Escape key
    this.keydownHandler = this.handleKeydown.bind(this);
    document.addEventListener('keydown', this.keydownHandler);
  }

  removeEventListeners() {
    if (this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler);
    }
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
    }
    if (this.popupClickHandler && this.popup) {
      this.popup.removeEventListener('click', this.popupClickHandler);
    }
  }

  /** @param {MouseEvent} e */
  handleTriggerClick(e) {
    e.stopPropagation();
    this.toggle();
  }

  /** @param {MouseEvent} e */
  handleDocumentClick(e) {
    const target = e.target;
    if (!(target instanceof Node)) return;
    if (this.isOpen && !this.contains(target)) {
      this.hide();
    }
  }

  /** @param {KeyboardEvent} e */
  handleKeydown(e) {
    if (e.key === 'Escape' && this.isOpen) {
      this.hide();
    }
  }

  /** @param {MouseEvent} e */
  handlePopupClick(e) {
    const target = e.target;
    if (!(target instanceof Element)) return;
    // Close if clicking on the overlay (the popup container itself)
    if (target === this.popup) {
      this.hide();
      return;
    }
    // Close if any ancestor has data-popup-close
    const closeEl = target.closest('[data-popup-close]');
    if (closeEl) {
      e.preventDefault();
      this.hide();
    }
  }

  show() {
    if (!this.popup || !this.trigger) return;
    
    this.closeOtherPopupsInGroup();
    
    this.isOpen = true;
    this.classList.add('popup-open');
    this.popup.classList.remove('popup--hidden');
    this.popup.setAttribute('aria-hidden', 'false');
    this.trigger.setAttribute('aria-expanded', 'true');
    
    const shopifySection = this.closest('.shopify-section');
    if (shopifySection instanceof HTMLElement) {
      shopifySection.style.zIndex = '10';
    }
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('popup:show', {
      bubbles: true,
      composed: true,
      detail: { popup: this }
    }));
  }

  closeOtherPopupsInGroup() {
    const parentContainer = this.closest('[data-block-type="group"]') || this.parentElement;
    if (!parentContainer) return;
    
    const allPopups = parentContainer.querySelectorAll('popup-element');
    allPopups.forEach(popupEl => {
      if (popupEl instanceof PopupElement && popupEl !== this && popupEl.isOpen) {
        popupEl.hide();
      }
    });
  }

  hide() {
    if (!this.popup || !this.trigger) return;
    
    this.isOpen = false;
    this.classList.remove('popup-open');
    this.popup.classList.add('popup--hidden');
    this.popup.setAttribute('aria-hidden', 'true');
    this.trigger.setAttribute('aria-expanded', 'false');
    
    const shopifySection = this.closest('.shopify-section');
    if (shopifySection instanceof HTMLElement) {
      shopifySection.style.zIndex = '';
    }
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('popup:hide', {
      detail: { popup: this }
    }));
  }

  // Public API methods
  openPopup() {
    this.show();
  }

  closePopup() {
    this.hide();
  }

  toggle() {
    if (this.isOpen) {
      this.hide();
    } else {
      this.show();
    }
  }

  /**
   * Initialize lookbook color swatches if present
   * Handles swatch selection, image updates, and slider functionality
   */
  initLookbookSwatches() {
    const swatchButtons = this.querySelectorAll('.lookbooks-banner-popup__color');
    if (swatchButtons.length === 0) return;

    const variantImage = this.querySelector('[data-initial-image]');
    const priceElement = this.querySelector('[data-price]');
    const productLink = this.querySelector('[data-product-link]');
    const productTitle = this.querySelector('[data-product-title]');

    swatchButtons.forEach(button => {
      if (!(button instanceof HTMLElement)) return;
      
      /** @param {Event} e */
      const handler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        swatchButtons.forEach(btn => btn.classList.remove('lookbooks-banner-popup__color--selected'));
        button.classList.add('lookbooks-banner-popup__color--selected');
        
        this.updateVariantData(button, { variantImage, priceElement, productTitle, productLink });
      };
      
      button.addEventListener('click', handler);
      this.swatchHandlers.set(button, handler);
    });

    this.initColorSliderDrag();
  }

  /**
   * Update variant data (image, price, title, link)
   * @param {HTMLElement} button 
   * @param {{ variantImage: Element | null, priceElement: Element | null, productTitle: Element | null, productLink: Element | null }} elements 
   */
  updateVariantData(button, elements) {
    const { variantImage, priceElement, productTitle, productLink } = elements;
    const { variantUrl, variantImage: imageUrl, variantPrice, variantTitle } = button.dataset;

    if (variantImage instanceof HTMLImageElement && imageUrl) {
      variantImage.style.opacity = '0.5';
      variantImage.src = imageUrl;
      variantImage.loading = 'eager';
      setTimeout(() => variantImage.style.opacity = '1', 50);
    }

    if (priceElement && variantPrice) priceElement.textContent = variantPrice;
    if (productTitle && variantTitle) productTitle.textContent = variantTitle;
    if (productLink instanceof HTMLAnchorElement && variantUrl) productLink.href = variantUrl;
  }

  initColorSliderDrag() {
    const colorSliders = this.querySelectorAll('.lookbooks-banner-popup__colors-slider');
    
    colorSliders.forEach(slider => {
      if (!(slider instanceof HTMLElement)) return;
      
      let isDown = false;
      let startX = 0;
      let scrollLeft = 0;

      /** @param {Event} e */
      const mousedownHandler = (e) => {
        if (!(e instanceof MouseEvent)) return;
        isDown = true;
        slider.style.cursor = 'grabbing';
        startX = e.pageX - slider.offsetLeft;
        scrollLeft = slider.scrollLeft;
      };

      const mouseleaveHandler = () => {
        isDown = false;
        slider.style.cursor = 'grab';
      };

      const mouseupHandler = () => {
        isDown = false;
        slider.style.cursor = 'grab';
      };

      /** @param {Event} e */
      const mousemoveHandler = (e) => {
        if (!isDown || !(e instanceof MouseEvent)) return;
        e.preventDefault();
        const x = e.pageX - slider.offsetLeft;
        const walk = (x - startX) * 2;
        slider.scrollLeft = scrollLeft - walk;
      };

      slider.addEventListener('mousedown', mousedownHandler);
      slider.addEventListener('mouseleave', mouseleaveHandler);
      slider.addEventListener('mouseup', mouseupHandler);
      slider.addEventListener('mousemove', mousemoveHandler);

      this.sliderHandlers.set(slider, {
        mousedown: mousedownHandler,
        mouseleave: mouseleaveHandler,
        mouseup: mouseupHandler,
        mousemove: mousemoveHandler
      });
    });
  }

  /**
   * Remove all lookbook swatch and slider event listeners
   */
  removeLookbookSwatchesListeners() {
    this.swatchHandlers.forEach((handler, button) => {
      button.removeEventListener('click', handler);
    });
    this.swatchHandlers.clear();

    this.sliderHandlers.forEach((handlers, slider) => {
      slider.removeEventListener('mousedown', handlers.mousedown);
      slider.removeEventListener('mouseleave', handlers.mouseleave);
      slider.removeEventListener('mouseup', handlers.mouseup);
      slider.removeEventListener('mousemove', handlers.mousemove);
    });
    this.sliderHandlers.clear();
  }

  get isPopupOpen() {
    return this.isOpen;
  }
}

if (!customElements.get('popup-element')) {
  customElements.define('popup-element', PopupElement);
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = PopupElement;
}
